import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCrm } from '../store';
import { useAuth } from '../auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, PageTitle } from '../ui';
import { formatEUR, formatDateFR, daysUntil } from '../utils';
import { Bell, CheckCircle2, TrendingUp, Users, Calendar, Euro, AlertTriangle, FileText } from 'lucide-react';
import PrestationForm from '../components/PrestationForm';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const COLORS = ['#f59e0b', '#10b981', '#0ea5e9', '#a855f7', '#ef4444', '#06b6d4', '#eab308', '#ec4899'];

export default function Dashboard() {
  const data = useCrm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [firstName, setFirstName] = useState('');
  const prest = data.prest;
  const factures = data.factures || [];
  const devis = data.devis || [];
  const [openForm, setOpenForm] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('user_id', user.id)
        .maybeSingle();
      const raw = (profile?.display_name || user.email?.split('@')[0] || '').trim();
      const first = raw.split(/\s+/)[0] || '';
      setFirstName(first.charAt(0).toUpperCase() + first.slice(1));
    })();
  }, [user]);

  // ============ KPI principaux ============
  const total = prest.length;
  const signed = prest.filter((p) => p.statut === 'Signé').length;
  const relance = prest.filter((p) => /relance/i.test(p.statut)).length;
  const isExclu = (p: any) => /^(Refus|Projet en pause|Expirée)/i.test(p.statut || '');
  const countWindow = (min: number, max: number) => prest.filter((p) => {
    if (isExclu(p)) return false;
    const d = daysUntil(p.dateEvent);
    return d != null && d >= min && d <= max;
  }).length;
  const j7 = countWindow(0, 7);
  const j15 = countWindow(0, 15);
  const j30 = countWindow(0, 30);
  const jPlus30 = prest.filter((p) => {
    if (isExclu(p)) return false;
    const d = daysUntil(p.dateEvent);
    return d != null && d < 0 && d >= -30;
  }).length;
  const caSigne = prest.filter((p) => p.statut === 'Signé').reduce((s, p) => s + (p.propTTC ?? 0), 0);

  // CA en cours = prestations non signées, hors refus / pause / expirée
  const caEnCours = useMemo(() => {
    return prest
      .filter((p) => !isExclu(p) && p.statut !== 'Signé')
      .reduce((s, p) => s + (p.propTTC ?? 0), 0);
  }, [prest]);

  // Reste à encaisser = factures non payées
  const resteAEncaisser = factures
    .filter((f) => !['Payée', 'Annulée'].includes(f.statut))
    .reduce((s, f) => {
      const ttc = f.lignes.reduce((acc, l) => acc + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0);
      return s + Math.max(0, ttc - (f.montantPaye || 0));
    }, 0);

  // Factures en retard
  const today = new Date().toISOString().slice(0, 10);
  const facturesEnRetard = factures.filter(
    (f) => !['Payée', 'Annulée', 'Brouillon'].includes(f.statut) && f.dateEcheance && f.dateEcheance < today,
  );

  // Devis actifs
  const devisActifs = devis.filter((d) => ['Envoyé', 'Relance', 'Brouillon'].includes(d.statut)).length;

  // Taux de conversion devis (Accepté ou Converti) / (total hors Brouillon)
  const tauxConversion = useMemo(() => {
    const consideres = devis.filter((d) => d.statut !== 'Brouillon');
    if (consideres.length === 0) return 0;
    const gagnes = consideres.filter((d) => ['Accepté', 'Converti'].includes(d.statut)).length;
    return Math.round((gagnes / consideres.length) * 100);
  }, [devis]);

  // ============ CA mensuel (12 derniers mois) sur prestations signées ============
  const caMensuel = useMemo(() => {
    const now = new Date();
    const map: Record<string, { mois: string; ca: number; nb: number }> = {};
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      map[key] = { mois: `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, ca: 0, nb: 0 };
    }
    prest
      .filter((p) => p.statut === 'Signé' && p.dateEvent)
      .forEach((p) => {
        const key = p.dateEvent.slice(0, 7);
        if (map[key]) {
          map[key].ca += p.propTTC ?? 0;
          map[key].nb += 1;
        }
      });
    return Object.values(map);
  }, [prest]);

  // ============ Prévisionnel : prochains 6 mois ============
  const previsionnel = useMemo(() => {
    const now = new Date();
    const map: Record<string, { mois: string; signe: number; potentiel: number }> = {};
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      map[key] = { mois: `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, signe: 0, potentiel: 0 };
    }
    prest
      .filter((p) => p.dateEvent && p.dateEvent >= today)
      .forEach((p) => {
        const key = p.dateEvent.slice(0, 7);
        if (!map[key]) return;
        const montant = p.propTTC ?? 0;
        if (p.statut === 'Signé') map[key].signe += montant;
        else if (/Propal|Relance|Demande|Contrat/i.test(p.statut)) map[key].potentiel += montant;
      });
    return Object.values(map);
  }, [prest, today]);

  // ============ Top clients (CA signé) ============
  const topClients = useMemo(() => {
    const map: Record<string, number> = {};
    prest
      .filter((p) => p.statut === 'Signé' && p.client)
      .forEach((p) => {
        map[p.client] = (map[p.client] ?? 0) + (p.propTTC ?? 0);
      });
    return Object.entries(map)
      .map(([nom, ca]) => ({ nom, ca }))
      .sort((a, b) => b.ca - a.ca)
      .slice(0, 5);
  }, [prest]);

  // ============ Top artistes (apparitions sur prestations signées) ============
  const topArtistes = useMemo(() => {
    const map: Record<string, { nb: number; ca: number }> = {};
    prest
      .filter((p) => p.statut === 'Signé')
      .forEach((p) => {
        (p.artistes || []).forEach((a) => {
          if (!map[a]) map[a] = { nb: 0, ca: 0 };
          map[a].nb += 1;
          map[a].ca += (p.propTTC ?? 0) / Math.max(1, (p.artistes || []).length);
        });
      });
    return Object.entries(map)
      .map(([nom, v]) => ({ nom, ...v }))
      .sort((a, b) => b.nb - a.nb)
      .slice(0, 6);
  }, [prest]);

  // ============ Mix services (prestations signées) ============
  const mixServices = useMemo(() => {
    const map: Record<string, number> = {};
    prest
      .filter((p) => p.statut === 'Signé')
      .forEach((p) => {
        (p.services || []).forEach((s) => {
          map[s] = (map[s] ?? 0) + 1;
        });
      });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [prest]);

  // ============ CA par canal de demande (Site Web, Téléphone, Bouche-à-oreille…) ============
  const caParDemande = useMemo(() => {
    const map: Record<string, { nb: number; ca: number }> = {};
    prest
      .filter((p) => p.statut === 'Signé')
      .forEach((p) => {
        const k = p.demande?.trim() || 'Non renseigné';
        if (!map[k]) map[k] = { nb: 0, ca: 0 };
        map[k].nb += 1;
        map[k].ca += p.propTTC ?? 0;
      });
    return Object.entries(map)
      .map(([nom, v]) => ({ nom, ...v }))
      .sort((a, b) => b.ca - a.ca);
  }, [prest]);

  // ============ CA par domaine artistique (signés) ============
  const caParDomaine = useMemo(() => {
    const map: Record<string, { nb: number; ca: number }> = {};
    prest
      .filter((p) => p.statut === 'Signé')
      .forEach((p) => {
        const doms = (p.domaines || []).filter(Boolean);
        if (doms.length === 0) {
          if (!map['Non renseigné']) map['Non renseigné'] = { nb: 0, ca: 0 };
          map['Non renseigné'].nb += 1;
          map['Non renseigné'].ca += p.propTTC ?? 0;
          return;
        }
        const part = (p.propTTC ?? 0) / doms.length;
        doms.forEach((d) => {
          if (!map[d]) map[d] = { nb: 0, ca: 0 };
          map[d].nb += 1;
          map[d].ca += part;
        });
      });
    return Object.entries(map)
      .map(([nom, v]) => ({ nom, ...v }))
      .sort((a, b) => b.ca - a.ca);
  }, [prest]);

  // ============ CA détaillé par artiste (tous, pas seulement top 6) ============
  const caParArtiste = useMemo(() => {
    const map: Record<string, { nb: number; ca: number }> = {};
    prest
      .filter((p) => p.statut === 'Signé')
      .forEach((p) => {
        const arts = (p.artistes || []).filter(Boolean);
        if (arts.length === 0) return;
        const part = (p.propTTC ?? 0) / arts.length;
        arts.forEach((a) => {
          if (!map[a]) map[a] = { nb: 0, ca: 0 };
          map[a].nb += 1;
          map[a].ca += part;
        });
      });
    return Object.entries(map)
      .map(([nom, v]) => ({ nom, ...v }))
      .sort((a, b) => b.ca - a.ca);
  }, [prest]);

  // ============ Top lieux (CDC §5.1) ============
  const topLieux = useMemo(() => {
    const map: Record<string, { nb: number; ca: number }> = {};
    prest
      .filter((p) => p.statut === 'Signé' && p.lieu)
      .forEach((p) => {
        const k = p.lieu.trim();
        if (!map[k]) map[k] = { nb: 0, ca: 0 };
        map[k].nb += 1;
        map[k].ca += p.propTTC ?? 0;
      });
    return Object.entries(map)
      .map(([nom, v]) => ({ nom, ...v }))
      .sort((a, b) => b.nb - a.nb)
      .slice(0, 6);
  }, [prest]);

  // ============ Délai moyen de signature (CDC §5.1) ============
  // Approximation : jours entre dateCreation prestation et passage Signé.
  // Comme on n'a pas la date exacte du passage Signé, on prend dateCreation → dateEvent
  // pour les prestations Signé (proxy raisonnable du cycle de vente).
  const delaiMoyenSignature = useMemo(() => {
    const ds: number[] = [];
    prest.filter((p) => p.statut === 'Signé' && p.dateCreation && p.dateEvent).forEach((p) => {
      const j = (new Date(p.dateEvent).getTime() - new Date(p.dateCreation).getTime()) / 86400000;
      if (j >= 0 && j < 365) ds.push(j);
    });
    if (!ds.length) return 0;
    return Math.round(ds.reduce((a, b) => a + b, 0) / ds.length);
  }, [prest]);

  // ============ Entonnoir transformation (CDC §5.1 : lead > devis > signature) ============
  const entonnoir = useMemo(() => {
    const leads = prest.length;
    const propals = prest.filter((p) => /Propal|Relance|Contrat|Signé/i.test(p.statut)).length;
    const signes = prest.filter((p) => p.statut === 'Signé').length;
    const tauxLeadDevis = leads ? Math.round((propals / leads) * 100) : 0;
    const tauxDevisSigne = propals ? Math.round((signes / propals) * 100) : 0;
    const tauxGlobal = leads ? Math.round((signes / leads) * 100) : 0;
    return { leads, propals, signes, tauxLeadDevis, tauxDevisSigne, tauxGlobal };
  }, [prest]);


  // Répartition par statut
  const statutCounts = prest.reduce<Record<string, number>>((acc, p) => {
    acc[p.statut] = (acc[p.statut] ?? 0) + 1;
    return acc;
  }, {});
  const sortedStatuts = Object.entries(statutCounts).sort((a, b) => b[1] - a[1]);
  const maxStatut = sortedStatuts[0]?.[1] ?? 1;

  const upcomingSigned = prest
    .filter((p) => p.statut === 'Signé' && p.dateEvent >= today)
    .sort((a, b) => a.dateEvent.localeCompare(b.dateEvent))
    .slice(0, 6);

  const priorityRelances = prest
    .filter((p) => /relance|propal/i.test(p.statut) || p.statut === 'Demande de Rdv')
    .sort((a, b) => a.dateEvent.localeCompare(b.dateEvent))
    .slice(0, 5);

  const statColor = (label: string) => {
    if (/Signé/i.test(label)) return 'text-emerald-400';
    if (/Relance/i.test(label)) return 'text-amber-400';
    if (/Demande/i.test(label)) return 'text-sky-400';
    if (/Propal/i.test(label)) return 'text-violet-400';
    if (/Refusée/i.test(label)) return 'text-red-400';
    if (/pause/i.test(label)) return 'text-slate-400';
    if (/Contrat/i.test(label)) return 'text-cyan-400';
    return 'text-slate-300';
  };
  const barColor = (label: string) => statColor(label).replace('text-', 'bg-');

  const tooltipStyle = {
    backgroundColor: '#0f172a',
    border: '1px solid #475569',
    borderRadius: 8,
    fontSize: 12,
    color: '#f1f5f9',
    boxShadow: '0 10px 30px -10px rgba(0,0,0,0.6)',
    padding: '8px 12px',
  };
  const tooltipItemStyle = { color: '#f1f5f9' };
  const tooltipLabelStyle = { color: '#cbd5e1', fontWeight: 600, marginBottom: 4 };

  return (
    <div>
      <PageTitle
        title={`Bonjour, ${firstName || 'Cygnature'} 👋`}
        subtitle="Vue d'ensemble de votre activité"
        action={
          <button
            onClick={() => setOpenForm(true)}
            className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-medium rounded-lg text-sm transition-colors"
          >
            + Nouvelle Prestation
          </button>
        }
      />

      <PrestationForm open={openForm} onOpenChange={setOpenForm} />

      {/* KPI Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-10 gap-3 mb-6">
        <StatCard label="ÉVÉNEMENTS" value={total} color="text-amber-400" icon={<Calendar size={14} />} onClick={() => navigate('/crm-pro-evenementiel/prestations')} />
        <StatCard label="SIGNÉS" value={signed} color="text-emerald-400" icon={<CheckCircle2 size={14} />} onClick={() => navigate('/crm-pro-evenementiel/prestations?statut=Sign%C3%A9')} />
        <StatCard label="RELANCES" value={relance} color="text-orange-400" icon={<Bell size={14} />} onClick={() => navigate('/crm-pro-evenementiel/relances')} />
        
        <StatCard label="DEVIS ACTIFS" value={devisActifs} color="text-cyan-400" icon={<FileText size={14} />} onClick={() => navigate('/crm-pro-evenementiel/devis')} />
        <StatCard label="CONVERSION" value={`${tauxConversion}%`} color="text-teal-400" icon={<TrendingUp size={14} />} onClick={() => navigate('/crm-pro-evenementiel/stats-avancees')} />
        <StatCard label="DÉLAI MOYEN" value={`${delaiMoyenSignature}j`} color="text-indigo-400" icon={<Calendar size={14} />} onClick={() => navigate('/crm-pro-evenementiel/stats-avancees')} />
        <StatCard label="EN RETARD" value={facturesEnRetard.length} color="text-rose-400" icon={<AlertTriangle size={14} />} onClick={() => navigate('/crm-pro-evenementiel/relances-factures')} />
        <StatCard label="J-7" value={j7} color="text-sky-300" icon={<TrendingUp size={14} />} onClick={() => navigate('/crm-pro-evenementiel/prestations?filtre=j7')} />
        <StatCard label="J-15" value={j15} color="text-sky-400" icon={<TrendingUp size={14} />} onClick={() => navigate('/crm-pro-evenementiel/prestations?filtre=j15')} />
        <StatCard label="J-30" value={j30} color="text-sky-500" icon={<TrendingUp size={14} />} onClick={() => navigate('/crm-pro-evenementiel/prestations?filtre=j30')} />
        <StatCard label="J+30" value={jPlus30} color="text-fuchsia-400" icon={<TrendingUp size={14} />} onClick={() => navigate('/crm-pro-evenementiel/prestations?filtre=jplus30')} />
        <StatCard label="CA SIGNÉ TTC" value={formatEUR(caSigne)} color="text-violet-400" small icon={<Euro size={14} />} onClick={() => navigate('/crm-pro-evenementiel/stats-avancees')} />
        <StatCard label="CA EN COURS" value={formatEUR(caEnCours)} color="text-amber-400" small icon={<FileText size={14} />} onClick={() => navigate('/crm-pro-evenementiel/devis')} />
        <StatCard label="À ENCAISSER" value={formatEUR(resteAEncaisser)} color="text-rose-400" small icon={<Euro size={14} />} onClick={() => navigate('/crm-pro-evenementiel/facturation')} />
      </div>

      {/* Graphiques principaux */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* CA mensuel */}
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs text-slate-400 uppercase tracking-widest">Chiffre d'affaires (12 derniers mois)</div>
            <div className="text-xs text-slate-500">Prestations signées TTC</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={caMensuel}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="mois" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  itemStyle={tooltipItemStyle}
                  labelStyle={tooltipLabelStyle}
                  formatter={(v: number) => formatEUR(v)}
                  cursor={{ fill: 'rgba(245, 158, 11, 0.08)' }}
                />
                <Bar dataKey="ca" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Mix services */}
        <Card className="p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4">Mix des services</div>
          <div className="h-64">
            {mixServices.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={mixServices} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={40}>
                    {mixServices.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-sm text-slate-500 italic h-full flex items-center justify-center">Aucune donnée</div>
            )}
          </div>
        </Card>
      </div>

      {/* Prévisionnel + Top clients */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4">Prévisionnel — 6 prochains mois</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={previsionnel}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="mois" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} formatter={(v: number) => formatEUR(v)} />
                <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                <Line type="monotone" dataKey="signe" name="Signé" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="potentiel" name="Potentiel" stroke="#a855f7" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Users size={12} /> Top clients (CA)
          </div>
          <div className="space-y-3">
            {topClients.map((c, i) => {
              const max = topClients[0]?.ca || 1;
              return (
                <div key={c.nom} className="text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-slate-300 truncate flex-1">{i + 1}. {c.nom}</span>
                    <span className="text-amber-400 text-xs ml-2">{formatEUR(c.ca)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(c.ca / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
            {!topClients.length && <div className="text-sm text-slate-500 italic">Aucun client signé.</div>}
          </div>
        </Card>
      </div>

      {/* Top artistes + Statuts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4">Top artistes (signés)</div>
          <div className="space-y-3">
            {topArtistes.map((a) => (
              <div key={a.nom} className="flex items-center justify-between text-sm border-b border-slate-800 pb-2 last:border-0">
                <div className="text-slate-200 truncate flex-1">{a.nom}</div>
                <div className="text-xs text-slate-500 mr-3">{a.nb} évts</div>
                <div className="text-xs text-emerald-400">{formatEUR(a.ca)}</div>
              </div>
            ))}
            {!topArtistes.length && <div className="text-sm text-slate-500 italic">Aucune donnée.</div>}
          </div>
        </Card>

        <Card className="lg:col-span-2 p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4">Répartition par statut</div>
          <div className="space-y-3">
            {sortedStatuts.map(([label, count]) => (
              <div key={label} className="flex items-center gap-3 text-sm">
                <div className="w-44 text-slate-400 text-right text-xs">{label}</div>
                <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-full ${barColor(label)} rounded-full`} style={{ width: `${(count / maxStatut) * 100}%` }} />
                </div>
                <div className="w-8 text-slate-300 text-xs">{count}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* CA détaillé : par artiste, canal de demande, domaine artistique */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <CaBreakdownCard
          title="CA par artiste"
          rows={caParArtiste}
          total={caSigne}
          accent="bg-amber-500"
          accentText="text-amber-400"
          emptyLabel="Aucun artiste signé"
        />
        <CaBreakdownCard
          title="CA par canal de demande"
          rows={caParDemande}
          total={caSigne}
          accent="bg-sky-500"
          accentText="text-sky-400"
          emptyLabel="Aucune demande renseignée"
        />
        <CaBreakdownCard
          title="CA par domaine artistique"
          rows={caParDomaine}
          total={caSigne}
          accent="bg-violet-500"
          accentText="text-violet-400"
          emptyLabel="Aucun domaine renseigné"
        />
      </div>

      {/* Entonnoir + Top lieux (CDC §5.1) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4">Entonnoir de transformation</div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-300">Leads (toutes prestations)</span>
                <span className="text-amber-400 font-medium">{entonnoir.leads}</span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-300">Devis / Propal envoyés</span>
                <span className="text-violet-400 font-medium">{entonnoir.propals} <span className="text-xs text-slate-500">({entonnoir.tauxLeadDevis}%)</span></span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500 rounded-full" style={{ width: `${entonnoir.tauxLeadDevis}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-300">Signés</span>
                <span className="text-emerald-400 font-medium">{entonnoir.signes} <span className="text-xs text-slate-500">({entonnoir.tauxDevisSigne}% des devis)</span></span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${entonnoir.tauxGlobal}%` }} />
              </div>
            </div>
            <div className="text-center text-xs text-slate-400 pt-2 border-t border-slate-800">
              Taux global lead → signé : <span className="text-emerald-400 font-medium text-sm">{entonnoir.tauxGlobal}%</span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4">Top lieux (signés)</div>
          <div className="space-y-3">
            {topLieux.map((l) => {
              const max = topLieux[0]?.nb || 1;
              return (
                <div key={l.nom} className="text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-slate-300 truncate flex-1">{l.nom}</span>
                    <span className="text-xs text-slate-500 ml-2">{l.nb} évts</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${(l.nb / max) * 100}%` }} />
                  </div>
                  <div className="text-[10px] text-emerald-400 text-right mt-0.5">{formatEUR(l.ca)}</div>
                </div>
              );
            })}
            {!topLieux.length && <div className="text-sm text-slate-500 italic">Aucune donnée.</div>}
          </div>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4">Prochains événements signés</div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto">
            {upcomingSigned.map((p) => {
              const d = new Date(p.dateEvent);
              return (
                <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/40 transition-colors">
                  <div className="text-center min-w-[40px]">
                    <div className="text-xl font-heading text-amber-400 leading-none">{d.getDate()}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '')}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-200 truncate">{p.client}</div>
                    <div className="text-xs text-slate-500 truncate">{p.lieu || '—'}</div>
                  </div>
                  <div className="text-xs text-emerald-400">{formatEUR(p.propTTC ?? 0)}</div>
                </div>
              );
            })}
            {!upcomingSigned.length && <div className="text-sm text-slate-500 italic">Aucun événement signé à venir.</div>}
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-xs text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Bell size={12} className="text-amber-400" /> Relances prioritaires
          </div>
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {priorityRelances.map((p) => (
              <div key={p.id} className="p-3 rounded-lg border border-slate-800 hover:border-slate-700 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Bell size={14} className="text-amber-400" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-200 truncate">{p.client}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {p.libelle} · <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[10px]">{p.statut}</span>
                  </div>
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap">{formatDateFR(p.dateEvent)}</div>
              </div>
            ))}
            {!priorityRelances.length && <div className="text-sm text-slate-500 italic">Aucune relance en cours.</div>}
          </div>
        </Card>
      </div>

      {facturesEnRetard.length > 0 && (
        <Card className="p-6 border-rose-500/30 bg-rose-500/5">
          <div className="text-xs text-rose-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <AlertTriangle size={14} /> Factures en retard ({facturesEnRetard.length})
          </div>
          <div className="space-y-2">
            {facturesEnRetard.slice(0, 5).map((f) => (
              <div key={f.id} className="flex items-center justify-between text-sm border-b border-rose-500/10 pb-2 last:border-0">
                <div>
                  <span className="text-slate-200">{f.numero}</span>
                  <span className="text-slate-500 ml-3">{f.clientNom}</span>
                </div>
                <div className="text-xs text-rose-400">Échue le {formatDateFR(f.dateEcheance)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
  small,
  icon,
  onClick,
}: {
  label: string;
  value: string | number;
  color: string;
  small?: boolean;
  icon?: React.ReactNode;
  onClick?: () => void;
}) {
  const clickable = !!onClick;
  const inner = (
    <Card className={`p-4 ${clickable ? 'hover:border-amber-500/40 hover:bg-slate-900/60 transition-colors' : ''}`}>
      <div className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-2 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`font-heading ${small ? 'text-xl' : 'text-2xl'} ${color}`}>{value}</div>
    </Card>
  );
  if (!clickable) return inner;
  return (
    <button type="button" onClick={onClick} className="text-left w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500/50 rounded-xl">
      {inner}
    </button>
  );
}

function CaBreakdownCard({
  title,
  rows,
  total,
  accent,
  accentText,
  emptyLabel,
  maxRows = 8,
}: {
  title: string;
  rows: Array<{ nom: string; nb: number; ca: number }>;
  total: number;
  accent: string;
  accentText: string;
  emptyLabel: string;
  maxRows?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const sum = rows.reduce((s, r) => s + r.ca, 0);
  const max = rows[0]?.ca || 1;
  const visible = expanded ? rows : rows.slice(0, maxRows);
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-slate-400 uppercase tracking-widest">{title}</div>
        <div className={`text-xs ${accentText} font-semibold`}>{formatEUR(sum)}</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500 italic">{emptyLabel}</div>
      ) : (
        <div className="space-y-2.5">
          {visible.map((r) => {
            const pct = total > 0 ? (r.ca / total) * 100 : 0;
            return (
              <div key={r.nom} className="text-sm">
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span className="text-slate-300 truncate flex-1" title={r.nom}>{r.nom}</span>
                  <span className="text-[10px] text-slate-500 shrink-0">{r.nb} évt{r.nb > 1 ? 's' : ''}</span>
                  <span className={`text-xs ${accentText} shrink-0 tabular-nums`}>{formatEUR(r.ca)}</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-full ${accent} rounded-full`} style={{ width: `${(r.ca / max) * 100}%` }} />
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">{pct.toFixed(1)} % du CA total</div>
              </div>
            );
          })}
          {rows.length > maxRows && (
            <button type="button" onClick={() => setExpanded((v) => !v)}
              className="w-full text-[11px] text-slate-400 hover:text-slate-200 pt-2 border-t border-slate-800">
              {expanded ? '↑ Réduire' : `↓ Afficher les ${rows.length - maxRows} autres`}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
